import React, { useState } from 'react'
import { Cell, Column, HeaderGroup, Row, useTable } from 'react-table'

import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

import Pagination from '@/components/ui/pagination/Pagination'

type BaseData = {
  id: string
}

export type SelectionTableProps<T extends BaseData> = {
  container?: React.ElementType
  columns: Column<T>[]
  data: T[]
  onSelect?: (selectedIds: string[]) => void
  readOnly?: boolean
  rowsPerPageOptions?: number[]
  selected: string[]
}

const defaultRowsPerPageOptions = [5, 10, 15]

export function SelectionTable<T extends BaseData>(props: SelectionTableProps<T>) {
  const { container = Paper, columns, data, onSelect, readOnly = false, rowsPerPageOptions = defaultRowsPerPageOptions, selected } = props

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<T>({
    columns,
    data,
  })

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = Math.max(0, page * rowsPerPage - rows.length)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
  }

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage)
    setPage(1)
  }

  const handleSelect = (row: Row<T>) => () => {
    const selectedIndex = selected.indexOf(row.original.id)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row.original.id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }

    onSelect?.(newSelected)
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = event.target.checked ? rows.map((row) => row.original.id) : []
    onSelect?.(newSelected)
  }

  return (
    <TableContainer component={container}>
      <Table sx={{ minWidth: 500 }}>
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<T>, index) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {!readOnly && (
                <TableCell key="header_select" padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < rows.length}
                    checked={rows.length > 0 && selected.length === rows.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {headerGroup.headers.map((column: HeaderGroup<T>) => {
                return (
                  <TableCell
                    {...column.getHeaderProps()}
                    align={column.align || 'left'}
                    scope="col"
                    sx={{ width: column.width, maxWidth: column.maxWidth, minWidth: column.minWidth, wordWrap: 'break-word' }}
                  >
                    {column.render('Header')}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {(rowsPerPage > 0 ? rows.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage) : rows).map((row) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()} key={row.original.id || row.id}>
                {!readOnly && (
                  <TableCell key={`cell_${row.index}_select`} padding="checkbox">
                    <Checkbox color="primary" checked={selected.indexOf(row.original.id) !== -1} onChange={handleSelect(row)} />
                  </TableCell>
                )}
                {row.cells.map((cell: Cell<T>) => (
                  <TableCell {...cell.getCellProps()} align={cell.column.align || 'left'}>
                    {cell.render('Cell')}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
          {emptyRows > 0 && (
            <TableRow style={{ height: 63 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={handleRowsPerPageChange}
          totalItems={rows.length}
        />
      </Box>
    </TableContainer>
  )
}

export default SelectionTable
